import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { servicesAPI } from '../services/api';
import { ArrowLeft, Clock, DollarSign, CheckCircle } from 'lucide-react';
import ShareButton from '../components/ShareButton';
import { Card, CardBody, Badge } from '../components/ui';

interface ServiceDetail {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  category: string;
  price: number;
  currency: string;
  deliveryTime?: string;
  isActive: boolean;
}

const ServiceDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [service, setService] = useState<ServiceDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchService();
    }
  }, [slug]);

  const fetchService = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getBySlug(slug!);
      setService(response.data.data);
    } catch (error) {
      console.error('Error fetching service:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: any = {
      'cv-review': 'CV Review',
      'application-assistance': 'Application Assistance',
      'interview-prep': 'Interview Preparation',
      'career-consultation': 'Career Consultation',
      'other': 'Other'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: any = {
      'cv-review': 'bg-blue-100 text-blue-700',
      'application-assistance': 'bg-green-100 text-green-700',
      'interview-prep': 'bg-purple-100 text-purple-700',
      'career-consultation': 'bg-orange-100 text-orange-700',
      'other': 'bg-gray-100 text-gray-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const features = [
    'Professional expertise',
    'Quick turnaround time',
    'Unlimited revisions',
    'Personalized feedback',
    '24/7 support'
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Service not found</h2>
          <Link to="/services" className="text-blue-600 hover:underline">Back to services</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/services" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to all services
        </Link>

        <Card className="overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
          <CardBody className="p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <span className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full capitalize font-medium mb-2 ${getCategoryColor(service.category)}`}>
                  {getCategoryLabel(service.category)}
                </span>
                <h1 className="text-3xl font-bold text-gray-900">{service.name}</h1>
              </div>
              <ShareButton 
                url={`/services/${service.slug}`} 
                title={service.name}
                description={service.description}
              />
            </div>

            <div className="flex items-baseline gap-4 mb-8 pb-6 border-b border-gray-100">
              <span className="text-5xl font-bold text-blue-600">${service.price}</span>
              <span className="text-gray-500">per service</span>
            </div>

            {service.deliveryTime && (
              <div className="flex items-center gap-2 text-gray-600 mb-8">
                <Clock className="w-5 h-5" />
                <span>Delivery time: {service.deliveryTime}</span>
              </div>
            )}

            {service.description && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 leading-relaxed">{service.description}</p>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What's Included</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            <Link to="/login" className="block">
              <button className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition">
                Request This Service
              </button>
            </Link>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ServiceDetail;
